<script lang="ts" setup>
import { ULink } from '#components'

const props = defineProps({
  costs: {
    default: undefined,
    type: Object as PropType<{ perCompany: number, perRequest: number }>,
  },
  hasBorders: {
    default: false,
    type: Boolean,
  },
  hasCopy: {
    default: true,
    type: Boolean,
  },
  hasDocs: {
    default: false,
    type: Boolean,
  },
  method: {
    default: 'GET',
    type: String as PropType<ApiEndpointMethodMethod>,
  },
  noVersion: {
    default: false,
    type: Boolean,
  },
  path: {
    required: true,
    type: String,
  },
  statuses: {
    default: () => [],
    type: Array as PropType<ApiEndpointStatus[]>,
  },
  to: {
    default: undefined,
    type: [String, Object] as PropType<RawLocation>,
  },
})

const { version } = useApiDoc()
const config = useRuntimeConfig()
const { t } = useI18n()

const finalUrl = computed(() => {
  return `${config.public.apis.public}/${props.noVersion ? '' : version.value}${props.path === '/' ? '' : props.path}`
})
</script>

<template>
  <Component
    :is="to ? ULink : 'div'"
    class="flex flex-col gap-2 whitespace-nowrap text-left text-sm tracking-normal"
    :to="to"
  >
    <div
      class="grid grid-cols-[auto,1fr,auto,auto] items-center gap-4"
      :class="{
        'shadow-xs overflow-auto rounded-lg p-3 ring-1 ring-gray-200 dark:ring-gray-800': hasBorders,
        'bg-gray-100 dark:bg-gray-900': hasBorders,
      }"
    >
      <ApiEndpointMethod
        class="-mr-2 flex-initial"
        :method="method"
      />

      <div class="overflow-x-auto">
        <div
          class="flex items-center gap-2 text-gray-800 dark:text-gray-200"
          :class="{ 'hover:underline': to }"
        >
          {{ finalUrl }}
        </div>
      </div>

      <div v-if="costs">
        <span class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
          <BaseCoin>
            <span class="text-xs">
              <template v-if="!costs.perCompany && !costs.perRequest">
                {{ t("globals.free") }}
              </template>

              <template v-else-if="costs.perCompany">
                {{ numbro(costs.perCompany) }} {{ t('perCompany', costs.perCompany) }}
              </template>

              <template v-else>
                {{ numbro(costs.perRequest) }} {{ t('perRequest', costs.perRequest) }}
              </template>
            </span>
          </BaseCoin>
        </span>
      </div>

      <UButton
        v-if="hasDocs"
        color="gray"
        :icon="getIcon('docs')"
        :label="t('globals.docs')"
        size="2xs"
        :ui="{ icon: { base: 'text-gray-600 dark:text-gray-400' } }"
      />

      <ButtonClipboard
        v-else-if="hasCopy"
        class="flex-initial"
        has-token
        :show-label="false"
        size="2xs"
        :string-to-copy="finalUrl"
      />
    </div>

    <div v-if="statuses.length">
      <div
        v-for="status in statuses"
        :key="status.code"
      >
        <span
          :class="{
            'text-amber-500': status.color === 'amber',
            'text-emerald-500': status.color === 'emerald',
            'text-rose-500': status.color === 'rose',
          }"
        >
          {{ status.code }}
        </span>
        - {{ status.description }}
      </div>
    </div>
  </Component>
</template>

<i18n lang="json">
{
  "en": {
    "perCompany": "credit per company | credits per company",
    "perRequest": "credit per request | credits per request"
  },
  "es": {
    "perCompany": "crédito por empresa | créditos por empresa",
    "perRequest": "crédito por solicitud | créditos por solicitud"
  },
  "fr": {
    "perCompany": "crédit par entreprise | crédits par entreprise",
    "perRequest": "crédit par requête | crédits par requête"
  }
}
</i18n>
